import * as ko from "knockout";
import {viewModel as CreateMessageTemplate} from "./create";
import {tplRaw} from "./mutable";
import {global, lm} from "../../appContext";
import {IFormTemplate, IMessage, IMessageType, IRuleset} from "../../index";
import {CREATING_MESSAGE, REQUEST_MESSAGE} from "../../constants/Language";
import {getCancelButton, getSubmitButton} from "../../constants/RouteDefinitions";
import {nsRoute} from "../../util/route";
import {nsRuleset} from "../ruleset/nsRuleset";
import PromiseRequest from "../../req/PromiseRequest";
import {decodeHtml, getObjectType, stripHtml} from "../../util/misc";
import {ObjectTypeId} from "../../constants";
import {nsMessages} from "./fn";
import {MutableMessageType} from "./MutableMessageType";
import ButtonOrder = nsRoute.ButtonOrder;
import MessageTypeId = nsMessages.MessageTypeId;
import create_ehub_template_rulesetHtml from './create-ehub-template-ruleset.html?raw';

const tplRuleset = create_ehub_template_rulesetHtml
export const template = tplRaw.replace('<!-- TEMPLATE_RULESET -->', tplRuleset)

export class viewModel extends CreateMessageTemplate {

    private formName!: string;
    private readonly req_getformtemplate: PromiseRequest = new PromiseRequest('getformtemplate')
    private readonly requests: PromiseRequest[] = []

    protected override setupSubscriptions() {
        this.kosub.subscribeChanged(this.data.messagetype, (newValue, oldValue) => {
            const formId = this.__routeParams?.['formid'];
            if (formId && [
                MessageTypeId.eAdmUserTask,
                MessageTypeId.eAdmManagerTask
            ].includes(newValue?.id as MessageTypeId) && isNaN(parseInt(this.data.subject()))) {
                this.data.subject(formId)
            }
            if (oldValue) {
                return
            }
            switch (this.typeChecker.getType()?.[0]) {
                case 'sms': {
                    this.data.body(lm.t('form.create.messageflow.body.sms') ?? '')
                    break;
                }
                default: {
                    this.data.body(lm.t('form.create.messageflow.body.email') ?? '')
                    break;
                }
            }
        });
        super.setupSubscriptions();
    }

    protected override init(): Promise<void> {
        this.isReady(false)
        this.messageData({message: REQUEST_MESSAGE})
        return Promise.resolve()
            .then(() => {
                const formId = this.__routeParams?.['formid'];
                if (!formId) {
                    return;
                }
                return this.req_getformtemplate.request({id: formId})
            })
            .then((data: IFormTemplate[] | undefined) => {
                const formTemplate = data?.[0];
                if (!formTemplate) {
                    return;
                }
                this.formTemplate(formTemplate)
                const formId = this.__routeParams?.['formid'] ?? '';
                this.formName = stripHtml(decodeHtml(this.formTemplate()!.name));
                this.data.name(lm.t(`form.create.messageflow.name|form.id:${formId}|form.name:${this.formName}`) ?? '')
                this.data.description(lm.t(`form.create.messageflow.description|form.id:${formId}|form.name:${this.formName}`) ?? '')
                this.data.subject(lm.t(`form.create.messageflow.subject|form.id:${formId}|form.name:${this.formName}`) ?? '')
            })
            .then(() => super.init())
    }

    protected override initMessageType(): Promise<void> {
        return this.req_getmessagetypes
            .request()
            .then((data: IMessageType[]) => {
                this.data.messagetype(data.find(o => o.id == MessageTypeId.eAdmUserMessage) ?? null)
                this.mutableMessageType = new MutableMessageType(this.data.messagetype, data);
            });
    }

    protected override setupPageButtons() {
        const cancelButton = getCancelButton(() => {
            global.eHubManager.fnReject('abort')
        });
        if (cancelButton) {
            this.pageButtons.add(cancelButton);
        }
        this.pageButtons.add({
            onClick: this.onPreviewClick.bind(this),
            enabled: this.isFormValid.bind(this),
            icon: 'fa-eye',
            label: 'preview',
            cssClass: 'action',
            order: ButtonOrder.PREVIEW,
        })
        this.pageButtons.add(getSubmitButton({
            attr: {type: 'submit'},
            onClick: this.onSubmit.bind(this),
            enabled: this.isFormValid.bind(this),
        }))
    }

    protected override isRulesetOptional = ko.pureComputed((): boolean => {
        return true
    })

    protected override readonly objecttypeid = ko.pureComputed((): ObjectTypeId | null => {
        return ObjectTypeId.FormData
    });

    override onSubmit() {
        this.isReady(false);
        this.messageData({message: CREATING_MESSAGE});

        const rules = [
            nsRuleset.generateRule({
                attribute: 'FormId',
                argumentoperator: 'Er lik',
                argument: this.__routeParams?.['formid'] ?? '',
                order: 1,
            }),
            nsRuleset.generateRule({
                attribute: 'EntryType',
                argumentoperator: 'Er lik',
                argument: 'Create',
                order: 2,
                booleanoperator: 'And',
            }),
        ];
        const formId = this.__routeParams?.['formid'] ?? '';
        const ruleset = nsRuleset.generateRuleset({
            ...(lm.t(`form.create.messageflow.ruleset.description|form.id:${formId}|form.name:${this.formName}`) != null
                ? {description: lm.t(`form.create.messageflow.ruleset.description|form.id:${formId}|form.name:${this.formName}`)!}
                : {}),
            ...(lm.t(`form.create.messageflow.ruleset.name|form.id:${formId}|form.name:${this.formName}`) != null
                ? {name: lm.t(`form.create.messageflow.ruleset.name|form.id:${formId}|form.name:${this.formName}`)!}
                : {}),
            objecttype: getObjectType(ObjectTypeId.FormData),
            rules: [],
        })

        return Promise.resolve()
            .then(() => {
                const p = new PromiseRequest('createruleset')
                this.requests.push(p)
                return p
                    .request({
                        jruleset: JSON.stringify(ruleset),
                    })
                    .then((data: IRuleset[]) => {
                        const ruleset = data[0];
                        if (!ruleset) {
                            return;
                        }
                        this.data.ruleset(ruleset);
                    })
                    .then(() => {
                        const selectedRuleset = this.data.ruleset();
                        if (!selectedRuleset) {
                            return;
                        }
                        return Promise.all(rules.map(o => {
                            const p = new PromiseRequest('createrule')
                            this.requests.push(p)
                            return p.request({
                                rulesetid: selectedRuleset.id,
                                jrule: JSON.stringify(o),
                            })
                        }))
                    })
            })
            .then(() => {
                const data = this.getSubmitData();
                data.body = encodeURIComponent(data.body)
                const jmessage = JSON.stringify(data);

                this.req_submit.abort();
                return this.req_submit
                    .request({jmessage: jmessage})
                    .then((data: IMessage[]) => {
                        const message = data[0];
                        if (message) {
                            this.data.$original = message;
                        }
                    })
            })
            .then(() => {
                global.cacheManager.removeCache('getruleset');
                global.cacheManager.removeCache('searchruleset');
                global.cacheManager.removeCache("searchmessage");
                global.cacheManager.removeCache("getmessage");
                global.cacheManager.removeCache('getehubprocess');
                global.cacheManager.removeCache('getformtemplate');

                // changes to the message flow could have changed one or more expressions in the subject/body -
                // therefore remove chache of any expressions evaluated
                global.cacheManager.removeCache('evaluateexpression');

                global.eHubManager.fnResolve(this.data.$original.id)
            })
            .catch((reason: string) => {
                global.eHubManager.fnReject(reason)
            });

    }

    override dispose() {
        super.dispose();
        this.requests.forEach(o => o.abort())
        this.req_getformtemplate.abort()
        global.eHubManager.fnReject?.('abort')
    }

}
